import type { ProductOption } from '@restaurant/api-contracts';
import { canMeetMinimum, effectiveSelectBounds, optionLimitsFit } from '@restaurant/types';

export type OptionRuleFields = Pick<
  ProductOption,
  'isRequired' | 'isMultiple' | 'minSelect' | 'maxSelect'
>;

type OptionWithChoices = OptionRuleFields & { choices: ReadonlyArray<ChoiceStock> };

interface ChoiceStock {
  isActive: boolean;
  inStock: boolean;
}

export interface OptionLimits {
  minSelect: number;
  maxSelect: number | null;
}

export type LimitSummary =
  | { kind: 'exactly'; n: number }
  | { kind: 'upTo'; n: number }
  | { kind: 'range'; min: number; max: number }
  | { kind: 'atLeast'; n: number }
  | { kind: 'any' };

export type LimitField = 'min' | 'max';

export type LimitInputError =
  | { kind: 'notInteger' }
  | { kind: 'range'; lo: number; hi: number }
  | { kind: 'minAboveMax'; max: number }
  | { kind: 'maxBelowMin'; min: number };

export type LimitInputResult =
  | { ok: true; limits: OptionLimits; isRequired: boolean }
  | { ok: false; error: LimitInputError };

export type LegacyIssue =
  | { kind: 'minAboveN'; min: number; n: number }
  | { kind: 'maxAboveN'; max: number; n: number }
  | { kind: 'minAboveMax'; min: number; max: number };

export type ClampChange = 'min' | 'max' | 'both';

export interface ClampPlan {
  limits: OptionLimits;
  isRequired: boolean | null;
  change: ClampChange;
  n: number;
  rule: LimitSummary;
}

export const activeChoiceCount = (option: OptionWithChoices): number =>
  option.choices.filter((c) => c.isActive).length;

export const inStockChoiceCount = (option: OptionWithChoices): number =>
  option.choices.filter((c) => c.isActive && c.inStock).length;

export const hasLimit = (option: OptionRuleFields): boolean =>
  option.isMultiple && (option.minSelect > 0 || option.maxSelect !== null);

export function summarizeSelection(option: OptionRuleFields): LimitSummary {
  const { min, max } = effectiveSelectBounds(option);
  if (max === null) return min > 0 ? { kind: 'atLeast', n: min } : { kind: 'any' };
  if (min === max) return { kind: 'exactly', n: max };
  if (min === 0) return { kind: 'upTo', n: max };
  return { kind: 'range', min, max };
}

export const limitRange = (field: LimitField, choiceCount: number): { lo: number; hi: number } =>
  field === 'min' ? { lo: 0, hi: choiceCount } : { lo: 1, hi: Math.max(choiceCount, 1) };

export function parseLimitInput(
  field: LimitField,
  raw: string,
  option: OptionRuleFields,
  choiceCount: number,
): LimitInputResult {
  const text = raw.trim();
  if (!/^\d+$/.test(text)) return { ok: false, error: { kind: 'notInteger' } };
  const value = Number(text);
  const { lo, hi } = limitRange(field, choiceCount);
  if (value < lo || value > hi) return { ok: false, error: { kind: 'range', lo, hi } };
  if (field === 'min') {
    if (option.maxSelect !== null && value > option.maxSelect) {
      return { ok: false, error: { kind: 'minAboveMax', max: option.maxSelect } };
    }
    return {
      ok: true,
      limits: { minSelect: value, maxSelect: option.maxSelect },
      isRequired: value >= 1,
    };
  }
  if (value < option.minSelect) {
    return { ok: false, error: { kind: 'maxBelowMin', min: option.minSelect } };
  }
  return {
    ok: true,
    limits: { minSelect: option.minSelect, maxSelect: value },
    isRequired: option.minSelect >= 1,
  };
}

export const defaultLimitOn = (
  option: OptionWithChoices,
): { limits: OptionLimits; isRequired: boolean } => {
  const total = activeChoiceCount(option);
  const max = Math.min(Math.max(inStockChoiceCount(option), 1), Math.max(total, 1));
  return { limits: { minSelect: 0, maxSelect: max }, isRequired: false };
};

export const limitOffBody = (): { limits: OptionLimits; isRequired: boolean } => ({
  limits: { minSelect: 0, maxSelect: null },
  isRequired: false,
});

export function legacyIssues(option: OptionWithChoices): LegacyIssue[] {
  const n = activeChoiceCount(option);
  if (n === 0) return [];
  const { minSelect: min, maxSelect: max } = option;
  if (optionLimitsFit({ minSelect: min, maxSelect: max }, n)) return [];
  const issues: LegacyIssue[] = [];
  if (min > n) issues.push({ kind: 'minAboveN', min, n });
  if (max !== null && min <= max && max > n) issues.push({ kind: 'maxAboveN', max, n });
  if (max !== null && min > max) issues.push({ kind: 'minAboveMax', min, max });
  return issues;
}

export const stockShortfall = (
  option: OptionWithChoices,
): { inStock: number; total: number } | null => {
  const inStock = inStockChoiceCount(option);
  return canMeetMinimum(option, inStock) ? null : { inStock, total: activeChoiceCount(option) };
};

export function planClampForRemoval(
  option: OptionWithChoices,
  choice: ChoiceStock,
): ClampPlan | null {
  if (!choice.isActive) return null;
  const n = activeChoiceCount(option) - 1;
  const { minSelect: min, maxSelect: max } = option;
  const nextMax = max === null ? null : Math.min(max, Math.max(n, 1));
  const nextMin = Math.min(min, n, nextMax ?? n);
  const minChanged = nextMin !== min;
  const maxChanged = nextMax !== max;
  if (!minChanged && !maxChanged) return null;
  const rule = summarizeSelection(option);
  return {
    limits: { minSelect: nextMin, maxSelect: nextMax },
    isRequired: option.isMultiple ? nextMin >= 1 : null,
    change: minChanged && maxChanged ? 'both' : minChanged ? 'min' : 'max',
    n,
    rule,
  };
}
